import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Chip,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  FormControlLabel,
  Radio,
  RadioGroup,
  Card,
  CardContent,
  Divider,
  Snackbar,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Add,
  Edit,
  Delete,
  Key,
  Refresh,
  Visibility,
} from '@mui/icons-material';
import {
  backofficeUserService,
  BackofficeUser,
  CreateBackofficeUserRequest,
  UpdateBackofficeUserRequest,
  AvailableRole,
} from '../../services/backofficeUserService';

const MOWILEX_COLORS = {
  primary: '#E31E24',
  primaryDark: '#B71C1C',
  primaryLight: '#FF5252',
  white: '#FFFFFF',
  lightGray: '#F5F5F5',
  darkGray: '#757575',
  success: '#4CAF50',
  warning: '#FF9800',
  error: '#F44336',
};

const UserManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<BackofficeUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('');
  const [availableRoles, setAvailableRoles] = useState<AvailableRole[]>([]);

  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState<{ open: boolean; user: BackofficeUser | null }>({
    open: false,
    user: null,
  });
  const [viewDialog, setViewDialog] = useState<{ open: boolean; user: BackofficeUser | null }>({
    open: false,
    user: null,
  });
  const [passwordDialog, setPasswordDialog] = useState<{ open: boolean; user: BackofficeUser | null }>({
    open: false,
    user: null,
  });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; user: BackofficeUser | null }>({
    open: false,
    user: null,
  });

  // Form states
  const [createForm, setCreateForm] = useState<CreateBackofficeUserRequest>({
    email: '',
    password: '',
    full_name: '',
    role: 'backoffice_user',
  });
  const [editForm, setEditForm] = useState<UpdateBackofficeUserRequest>({
    full_name: '',
    role: 'backoffice_user',
    is_active: true,
  });
  const [newPassword, setNewPassword] = useState('');

  // Loading states
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    loadUsers();
    loadAvailableRoles();
  }, [page, rowsPerPage, search, roleFilter, activeFilter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      
      const isActiveFilter = activeFilter === 'active' ? true : activeFilter === 'inactive' ? false : undefined;
      
      const response = await backofficeUserService.getUsers(
        page + 1,
        rowsPerPage,
        search || undefined,
        roleFilter || undefined,
        isActiveFilter
      );
      
      setUsers(response.users);
      setTotal(response.total);
    } catch (error: any) {
      setError(error.message || t('backoffice.userManagement.messages.loadUsersFailed'));
      setUsers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableRoles = async () => {
    try {
      const response = await backofficeUserService.getAvailableRoles();
      setAvailableRoles(response.roles);
    } catch (error: any) {
      console.error('Failed to load available roles:', error.message);
    }
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const handleRoleFilter = (role: string) => {
    setRoleFilter(role);
    setPage(0);
  };

  const handleActiveFilter = (active: string) => {
    setActiveFilter(active);
    setPage(0);
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'super_admin':
        return t('backoffice.userManagement.roles.superAdmin');
      case 'backoffice_admin':
        return t('backoffice.userManagement.roles.admin');
      case 'backoffice_user':
        return t('backoffice.userManagement.roles.user');
      default:
        return role;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'error';
      case 'backoffice_admin':
        return 'warning';
      case 'backoffice_user':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('id-ID', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const handleCreateUser = async () => {
    try {
      setCreateLoading(true);
      await backofficeUserService.createUser(createForm);
      setSuccess(t('backoffice.userManagement.messages.userCreated'));
      setCreateDialog(false);
      setCreateForm({
        email: '',
        password: '',
        full_name: '',
        role: 'backoffice_user',
      });
      loadUsers();
    } catch (error: any) {
      setError(error.message || t('backoffice.userManagement.messages.createUserFailed'));
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!editDialog.user) return;
    
    try {
      setUpdateLoading(true);
      await backofficeUserService.updateUser(editDialog.user.id, editForm);
      setSuccess(t('backoffice.userManagement.messages.userUpdated'));
      setEditDialog({ open: false, user: null });
      setEditForm({
        full_name: '',
        role: 'backoffice_user',
        is_active: true,
      });
      loadUsers();
    } catch (error: any) {
      setError(error.message || t('backoffice.userManagement.messages.updateUserFailed'));
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteDialog.user) return;
    
    try {
      setDeleteLoading(true);
      await backofficeUserService.deleteUser(deleteDialog.user.id);
      setSuccess(t('backoffice.userManagement.messages.userDeactivated'));
      setDeleteDialog({ open: false, user: null });
      loadUsers();
    } catch (error: any) {
      setError(error.message || t('backoffice.userManagement.messages.deactivateUserFailed'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!passwordDialog.user || !newPassword.trim()) return;
    
    try {
      setPasswordLoading(true);
      await backofficeUserService.resetUserPassword(passwordDialog.user.id, newPassword);
      setSuccess(t('backoffice.userManagement.messages.passwordReset'));
      setPasswordDialog({ open: false, user: null });
      setNewPassword('');
    } catch (error: any) {
      setError(error.message || t('backoffice.userManagement.messages.resetPasswordFailed'));
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 4, 
          mb: 3, 
          backgroundColor: '#FAFAFA',
          borderLeft: `4px solid ${MOWILEX_COLORS.primary}`,
          borderRadius: 2,
          border: '1px solid #E0E0E0'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                color: '#1A1A1A', 
                fontWeight: 600,
                fontSize: '1.75rem'
              }}
            >
              {t('backoffice.userManagement.title')}
            </Typography>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: '#666666',
                fontSize: '0.95rem',
                mt: 0.5
              }}
            >
              {t('backoffice.userManagement.subtitle')}
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadUsers}
              disabled={loading}
              sx={{
                borderColor: MOWILEX_COLORS.primary,
                color: MOWILEX_COLORS.primary,
                '&:hover': {
                  backgroundColor: `${MOWILEX_COLORS.primary}10`,
                  borderColor: MOWILEX_COLORS.primaryDark,
                }
              }}
            >
              {t('backoffice.userManagement.actions.refresh')}
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateDialog(true)}
              sx={{
                backgroundColor: MOWILEX_COLORS.primary,
                '&:hover': {
                  backgroundColor: MOWILEX_COLORS.primaryDark,
                }
              }}
            >
              {t('backoffice.userManagement.addUser')}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Alerts */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder={t('backoffice.userManagement.searchPlaceholder')}
            size="small"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          
          <TextField
            select
            label={t('backoffice.userManagement.table.role')}
            size="small"
            value={roleFilter}
            onChange={(e) => handleRoleFilter(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">{t('backoffice.userManagement.filters.allRoles')}</MenuItem>
            {availableRoles.map((role) => (
              <MenuItem key={role.value} value={role.value}>
                {role.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label={t('backoffice.userManagement.table.status')}
            size="small"
            value={activeFilter}
            onChange={(e) => handleActiveFilter(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">{t('backoffice.userManagement.filters.allStatus')}</MenuItem>
            <MenuItem value="active">{t('backoffice.userManagement.status.active')}</MenuItem>
            <MenuItem value="inactive">{t('backoffice.userManagement.status.inactive')}</MenuItem>
          </TextField>
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('backoffice.userManagement.table.name')}</TableCell>
              <TableCell>{t('backoffice.userManagement.table.email')}</TableCell>
              <TableCell>{t('backoffice.userManagement.table.role')}</TableCell>
              <TableCell>{t('backoffice.userManagement.table.status')}</TableCell>
              <TableCell>{t('backoffice.userManagement.table.lastLogin')}</TableCell>
              <TableCell>{t('backoffice.userManagement.table.created')}</TableCell>
              <TableCell>{t('backoffice.userManagement.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary">{t('backoffice.userManagement.noUsersFound')}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Typography fontWeight="bold">{user.full_name}</Typography>
                    {user.phone && (
                      <Typography variant="caption" color="textSecondary">
                        {user.phone}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={getRoleLabel(user.role)}
                      color={getRoleColor(user.role)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.is_active ? t('backoffice.userManagement.status.active') : t('backoffice.userManagement.status.inactive')}
                      color={user.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(user.last_login_at)}</TableCell>
                  <TableCell>{formatDate(user.created_at)}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title={t('backoffice.userManagement.actions.viewDetails')}>
                        <IconButton
                          size="small"
                          onClick={() => setViewDialog({ open: true, user })}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('backoffice.userManagement.actions.editUser')}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditForm({
                              full_name: user.full_name,
                              role: user.role,
                              is_active: user.is_active,
                            });
                            setEditDialog({ open: true, user });
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('backoffice.userManagement.actions.resetPassword')}>
                        <IconButton
                          size="small"
                          onClick={() => setPasswordDialog({ open: true, user })}
                          sx={{ color: MOWILEX_COLORS.warning }}
                        >
                          <Key />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('backoffice.userManagement.actions.deactivateUser')}>
                        <IconButton
                          size="small"
                          onClick={() => setDeleteDialog({ open: true, user })}
                          disabled={!user.is_active}
                          sx={{ color: MOWILEX_COLORS.error }}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Create User Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('backoffice.userManagement.dialogs.createUser')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label={t('backoffice.userManagement.form.fullName')}
              value={createForm.full_name}
              onChange={(e) => setCreateForm(prev => ({ ...prev, full_name: e.target.value }))}
              required
            />
            <TextField
              fullWidth
              label={t('backoffice.userManagement.form.email')}
              type="email"
              value={createForm.email}
              onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
              required
            />
            <TextField
              fullWidth
              label={t('backoffice.userManagement.form.password')}
              type="password"
              value={createForm.password}
              onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
              required
              helperText={t('backoffice.userManagement.form.passwordHelper')}
            />
            <TextField
              select
              fullWidth
              label={t('backoffice.userManagement.form.role')}
              value={createForm.role}
              onChange={(e) => setCreateForm(prev => ({ ...prev, role: e.target.value as any }))}
              required
            >
              {availableRoles.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>{t('backoffice.userManagement.actions.cancel')}</Button>
          <Button 
            onClick={handleCreateUser}
            variant="contained" 
            disabled={createLoading || !createForm.email || !createForm.password || !createForm.full_name}
            startIcon={createLoading ? <CircularProgress size={16} /> : <Add />}
            sx={{
              backgroundColor: MOWILEX_COLORS.primary,
              '&:hover': {
                backgroundColor: MOWILEX_COLORS.primaryDark,
              }
            }}
          >
            {createLoading ? t('backoffice.userManagement.actions.creating') : t('backoffice.userManagement.actions.createUser')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, user: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{t('backoffice.userManagement.dialogs.editUser')}</DialogTitle>
        <DialogContent>
          {editDialog.user && (
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label={t('backoffice.userManagement.form.fullName')}
                value={editForm.full_name}
                onChange={(e) => setEditForm(prev => ({ ...prev, full_name: e.target.value }))}
              />
              <TextField
                select
                fullWidth
                label={t('backoffice.userManagement.form.role')}
                value={editForm.role}
                onChange={(e) => setEditForm(prev => ({ ...prev, role: e.target.value as any }))}
              >
                {availableRoles.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </TextField>
              <FormControl component="fieldset">
                <FormLabel component="legend">{t('backoffice.userManagement.form.status')}</FormLabel>
                <RadioGroup
                  value={editForm.is_active}
                  onChange={(e) => setEditForm(prev => ({ ...prev, is_active: e.target.value === 'true' }))}
                >
                  <FormControlLabel value={true} control={<Radio />} label={t('backoffice.userManagement.status.active')} />
                  <FormControlLabel value={false} control={<Radio />} label={t('backoffice.userManagement.status.inactive')} />
                </RadioGroup>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, user: null })}>{t('backoffice.userManagement.actions.cancel')}</Button>
          <Button 
            onClick={handleUpdateUser}
            variant="contained" 
            disabled={updateLoading}
            startIcon={updateLoading ? <CircularProgress size={16} /> : <Edit />}
            sx={{
              backgroundColor: MOWILEX_COLORS.primary,
              '&:hover': {
                backgroundColor: MOWILEX_COLORS.primaryDark,
              }
            }}
          >
            {updateLoading ? t('backoffice.userManagement.actions.updating') : t('backoffice.userManagement.actions.updateUser')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View User Dialog */}
      <Dialog open={viewDialog.open} onClose={() => setViewDialog({ open: false, user: null })} maxWidth="md" fullWidth>
        <DialogTitle>{t('backoffice.userManagement.dialogs.userDetails')}</DialogTitle>
        <DialogContent>
          {viewDialog.user && (
            <Box sx={{ mt: 2 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>{t('backoffice.userManagement.form.basicInformation')}</Typography>
                  <Typography><strong>{t('backoffice.userManagement.form.name')}:</strong> {viewDialog.user.full_name}</Typography>
                  <Typography><strong>{t('backoffice.userManagement.form.email')}:</strong> {viewDialog.user.email}</Typography>
                  <Typography><strong>{t('backoffice.userManagement.form.phone')}:</strong> {viewDialog.user.phone || t('backoffice.userManagement.form.notProvided')}</Typography>
                  <Typography><strong>{t('backoffice.userManagement.form.role')}:</strong> {getRoleLabel(viewDialog.user.role)}</Typography>
                  <Typography><strong>{t('backoffice.userManagement.form.status')}:</strong> {viewDialog.user.is_active ? t('backoffice.userManagement.status.active') : t('backoffice.userManagement.status.inactive')}</Typography>
                  <Typography><strong>{t('backoffice.userManagement.form.verified')}:</strong> {viewDialog.user.is_verified ? t('backoffice.userManagement.form.yes') : t('backoffice.userManagement.form.no')}</Typography>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="h6" gutterBottom>{t('backoffice.userManagement.form.activity')}</Typography>
                  <Typography><strong>{t('backoffice.userManagement.form.lastLogin')}:</strong> {formatDate(viewDialog.user.last_login_at)}</Typography>
                  <Typography><strong>{t('backoffice.userManagement.form.created')}:</strong> {formatDate(viewDialog.user.created_at)}</Typography>
                  <Typography><strong>{t('backoffice.userManagement.form.updated')}:</strong> {formatDate(viewDialog.user.updated_at)}</Typography>
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog({ open: false, user: null })}>{t('backoffice.userManagement.actions.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={passwordDialog.open} onClose={() => setPasswordDialog({ open: false, user: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{t('backoffice.userManagement.dialogs.resetPassword')}</DialogTitle>
        <DialogContent>
          {passwordDialog.user && (
            <Box sx={{ pt: 2 }}>
              <Typography gutterBottom>
                {t('backoffice.userManagement.dialogs.resetPasswordFor', { name: passwordDialog.user.full_name, email: passwordDialog.user.email })}
              </Typography>
              <TextField
                fullWidth
                label={t('backoffice.userManagement.form.newPassword')}
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
                helperText={t('backoffice.userManagement.form.passwordHelper')}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog({ open: false, user: null })}>{t('backoffice.userManagement.actions.cancel')}</Button>
          <Button 
            onClick={handleResetPassword}
            variant="contained" 
            disabled={passwordLoading || newPassword.length < 8}
            startIcon={passwordLoading ? <CircularProgress size={16} /> : <Key />}
            sx={{
              backgroundColor: MOWILEX_COLORS.warning,
              '&:hover': {
                backgroundColor: '#E68900',
              }
            }}
          >
            {passwordLoading ? t('backoffice.userManagement.actions.resetting') : t('backoffice.userManagement.actions.resetPassword')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, user: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{t('backoffice.userManagement.dialogs.deactivateUser')}</DialogTitle>
        <DialogContent>
          {deleteDialog.user && (
            <Typography>
              {t('backoffice.userManagement.dialogs.confirmDeactivate', { name: deleteDialog.user.full_name, email: deleteDialog.user.email })}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, user: null })}>{t('backoffice.userManagement.actions.cancel')}</Button>
          <Button 
            onClick={handleDeleteUser}
            variant="contained" 
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : <Delete />}
            sx={{
              backgroundColor: MOWILEX_COLORS.error,
              '&:hover': {
                backgroundColor: '#D32F2F',
              }
            }}
          >
            {deleteLoading ? t('backoffice.userManagement.actions.deactivating') : t('backoffice.userManagement.actions.deactivateUser')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagementPage;